import { useState } from 'react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { enqueueCommand } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  Field,
  Input,
  Sheet,
  useToast,
} from '@/ui'
import { MIXING_REASONS, type MixingReason, type ReportMixingPayload } from '../../commands'
import { runParentIds, type ProcessState, type RunView } from '../../projection'
import { runFigures } from '../../model/rules'
import { Line } from '../../components/bits'
import type { RunDraft, RunDraftUpdate } from './draft'

/** F6.4 — Run in progress: settings, mixing in one tap, and Machine stopped. */
export function RunningSection({
  state,
  run,
  draft,
  update,
  onStopped,
}: {
  state: ProcessState
  run: RunView
  draft: RunDraft
  update: RunDraftUpdate
  onStopped: () => void
}) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const step = state.steps.get(run.step_code)
  const fig = runFigures(run, step)
  const [mixOpen, setMixOpen] = useState(false)
  const [reason, setReason] = useState<MixingReason | null>(null)
  const [saving, setSaving] = useState(false)

  const reportMixing = async () => {
    if (!reason) return
    setSaving(true)
    try {
      const payload: ReportMixingPayload = { run_id: run.id, reason_code: reason, note: null }
      await enqueueCommand({
        type: 'report_mixing',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setMixOpen(false)
      toast.success(t('process.mixing.saved'))
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="py-1">
          <Line
            label={t('process.running.started')}
            value={run.started_at ? fmt.time(run.started_at) : t('process.running.notStarted')}
          />
          <Line
            label={t('process.running.inSoFar')}
            value={`${t('process.bagsIn', { n: run.inputs.length })} · ${fmt.weight(fig.input_g)}`}
          />
        </CardBody>
      </Card>

      {(step?.settings_fields.length ?? 0) > 0 && (
        <Card>
          <CardHeader eyebrow={<BilingualLabel k="process.running.settings" />} />
          <CardBody className="flex flex-col gap-3">
            {step!.settings_fields.map((f) => (
              <Field key={f.key} label={locale === 'hi' ? f.hi : f.en} aside={f.unit ?? undefined}>
                <Input
                  size="touch"
                  value={draft.params[f.key] ?? ''}
                  onChange={(e) => {
                    const v = e.target.value.slice(0, 40)
                    update((d) => ({ ...d, params: { ...d.params, [f.key]: v } }))
                  }}
                />
              </Field>
            ))}
            <p className="text-[12px] text-muted">{t('process.running.settingsHint')}</p>
          </CardBody>
        </Card>
      )}

      {run.mixing_reason ? (
        <Banner tone="error">{t(`process.mixing.reason.${run.mixing_reason}` as MessageKey)}</Banner>
      ) : (
        <Button variant="danger" size="touch" fullWidth onClick={() => setMixOpen(true)}>
          <BilingualLabel k="process.mixing.button" />
        </Button>
      )}

      {draft.stopped_at ? (
        <Banner
          tone="success"
          action={
            <Button size="sm" variant="ghost" onClick={() => update((d) => ({ ...d, stopped_at: null }))}>
              {t('process.running.undoStop')}
            </Button>
          }
        >
          {t('process.running.stoppedAt', { time: fmt.time(draft.stopped_at) })}
        </Banner>
      ) : (
        <>
          <p className="text-[13px] text-muted">{t('process.running.sweepHint')}</p>
          <Button
            size="xl"
            fullWidth
            disabled={run.inputs.length === 0}
            onClick={() => {
              update((d) => ({ ...d, stopped_at: new Date().toISOString() }))
              onStopped()
            }}
          >
            <BilingualLabel k="process.running.stopped" />
          </Button>
        </>
      )}

      <Sheet
        open={mixOpen}
        onClose={() => setMixOpen(false)}
        title={<BilingualLabel k="process.mixing.button" />}
        description={t('process.mixing.hint')}
        footer={
          <Button variant="danger" size="xl" fullWidth disabled={!reason} loading={saving} onClick={() => void reportMixing()}>
            <BilingualLabel k="process.mixing.confirm" />
          </Button>
        }
      >
        <ChoiceGroup
          layout="stack"
          options={MIXING_REASONS.map((r) => ({ value: r, label: t(`process.mixing.reason.${r}` as MessageKey) }))}
          value={reason}
          onChange={setReason}
        />
      </Sheet>
    </div>
  )
}
